import { Component, ElementRef, EventEmitter, HostListener, Input, Output, ViewChild, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
  selector: 'app-search-field',
  templateUrl: './search-field.component.html',
  styleUrls: ['./search-field.component.scss'],
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => SearchFieldComponent),
    multi: true
  }]
})
export class SearchFieldComponent implements ControlValueAccessor {

  @Input() placeholderKey = '';
  @Input() variant: 'default' | 'compact' = 'default';
  @Input() disabled = false;
  @Output() search = new EventEmitter<string>();

  @ViewChild('input', { static: true }) private inputRef: ElementRef<HTMLInputElement>;
  @ViewChild('placeholderText') private placeholderTextRef: ElementRef<HTMLElement>;

  value = '';
  placeholderOverflows = false;

  private propagateChange: (value: string) => void = () => {};
  private propagateTouched: () => void = () => {};

  onInput(value: string): void {
    this.value = value;
    this.propagateChange(value);
    this.search.emit(value);
  }

  onBlur(): void {
    this.propagateTouched();
  }

  updatePlaceholderOverflow(): void {
    const text = this.placeholderTextRef;
    this.placeholderOverflows = !!text
      && text.nativeElement.offsetWidth > this.inputRef.nativeElement.clientWidth;
  }

  @HostListener('document:keydown', ['$event'])
  onDocumentKeydown(event: KeyboardEvent): void {
    if (this.disabled || event.key !== '/' || this.isTextEntry(event.target)) {
      return;
    }
    if (!this.isFirstOnScreen()) {
      return;
    }
    event.preventDefault();
    this.inputRef.nativeElement.focus();
  }

  writeValue(value: string): void {
    this.value = value || '';
  }

  registerOnChange(fn: (value: string) => void): void {
    this.propagateChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.propagateTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  // Kazde pole slucha tego samego klawisza, a ekrany miewaja ich kilka. O fokus decyduje
  // kolejnosc w dokumencie, przy czym otwarty modal ma pierwszenstwo przed tlem.
  private isFirstOnScreen(): boolean {
    const visible = Array.from(document.querySelectorAll<HTMLInputElement>('.search-field__input'))
      .filter(input => !input.disabled && input.offsetParent !== null);
    const inModal = visible.filter(input => !!input.closest('.modal'));
    const candidates = inModal.length ? inModal : visible;
    return candidates[0] === this.inputRef.nativeElement;
  }

  private isTextEntry(target: EventTarget): boolean {
    const element = target as HTMLElement;
    if (!element || !element.tagName) {
      return false;
    }
    return element.tagName === 'INPUT'
      || element.tagName === 'TEXTAREA'
      || element.isContentEditable === true;
  }
}
